/**
 * fileTypeConfig.ts
 *
 * Maps MIME types to their visual config for document cards.
 * Adjust color tokens using values from src/core/lib/color-palette.ts.
 * Adjust type labels via the 'fileType.*' keys in locales/es.json + en.json.
 */

import { File, FileText, Image, Sheet, Video, Archive, Music, Code2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface FileTypeConfig {
  /** Color token from the system palette (src/core/lib/color-palette.ts) */
  token: string;
  /** i18n key within the 'projectFiles' namespace (e.g. 'fileType.pdf') */
  typeKey: string;
  icon: LucideIcon;
}

export const FILE_TYPE_CONFIGS: Record<string, FileTypeConfig> = {
  image: { token: 'cyan-glacier', typeKey: 'fileType.image', icon: Image },
  pdf: { token: 'red-ember', typeKey: 'fileType.pdf', icon: FileText },
  sheet: { token: 'green-emerald', typeKey: 'fileType.sheet', icon: Sheet },
  slides: { token: 'orange-blaze', typeKey: 'fileType.slides', icon: FileText },
  document: { token: 'indigo-nebula', typeKey: 'fileType.document', icon: FileText },
  video: { token: 'purple-orchid', typeKey: 'fileType.video', icon: Video },
  audio: { token: 'pink-bloom', typeKey: 'fileType.audio', icon: Music },
  archive: { token: 'yellow-gold', typeKey: 'fileType.archive', icon: Archive },
  code: { token: 'lime-zest', typeKey: 'fileType.code', icon: Code2 },
  file: { token: 'teal-lagoon', typeKey: 'fileType.file', icon: File },
};

export function getFileTypeConfig(mimeType: string | null): FileTypeConfig {
  if (!mimeType) return FILE_TYPE_CONFIGS.file;
  const m = mimeType.toLowerCase();
  if (m.startsWith('image/')) return FILE_TYPE_CONFIGS.image;
  if (m.includes('pdf')) return FILE_TYPE_CONFIGS.pdf;
  if (
    m.includes('spreadsheet') ||
    m.includes('excel') ||
    m.includes('csv') ||
    m.includes('numbers')
  )
    return FILE_TYPE_CONFIGS.sheet;
  if (m.includes('presentation') || m.includes('powerpoint') || m.includes('keynote'))
    return FILE_TYPE_CONFIGS.slides;
  if (m.includes('word') || m.includes('document') || m.startsWith('text/'))
    return FILE_TYPE_CONFIGS.document;
  if (m.startsWith('video/')) return FILE_TYPE_CONFIGS.video;
  if (m.startsWith('audio/')) return FILE_TYPE_CONFIGS.audio;
  if (m.includes('zip') || m.includes('archive') || m.includes('tar') || m.includes('gzip'))
    return FILE_TYPE_CONFIGS.archive;
  if (
    m.includes('javascript') ||
    m.includes('json') ||
    m.includes('html') ||
    m.includes('css') ||
    m.includes('xml')
  )
    return FILE_TYPE_CONFIGS.code;
  return FILE_TYPE_CONFIGS.file;
}
